// ** React Imports
import { ChangeEvent, useState } from 'react'

// ** MUI Imports
import Box, { BoxProps } from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import OutlinedInput from '@mui/material/OutlinedInput'
import { styled, useTheme } from '@mui/material/styles'
import FormHelperText from '@mui/material/FormHelperText'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import FileUploaderMultiple from 'src/views/forms/file-uploader/FileUploaderMultiple'
import { DropzoneWrapper } from './DropzoneStyle'
import * as yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined'

// ** Type Imports
import {
  CustomRadioIconsData,
  CustomRadioIconsProps
} from 'src/@core/components/custom-radio/types'

// ** Custom Components Imports
import CustomChip from 'src/@core/components/mui/chip'
import CustomRadioIcons from 'src/@core/components/custom-radio/icons'
import Card from '@mui/material/Card'
import React from 'react'
import { useMutation, useQuery } from 'react-query'
import {
  createGenePanel,
  createGeneSets,
  createProject,
  editGenePanel,
  editGeneSets,
  fetchProjects,
  GenePanel,
  GeneSets,
  getGenePanel,
  getGeneSets,
  Project,
  SentieonPipelines,
  updateProject,
  upload
} from '@/@core/services/app.service'
import { t } from 'i18next'
import { useTranslation } from 'react-i18next'
import Translations from '@/layouts/components/Translations'
import PositionedSnackbar from '@/@core/components/message'
import RolDialog from '@/views/setting/DialogRol'
import PanelDialog from '@/views/setting/DialogPanel'
import ProjectDialog from '../DialogProject'
import {
  Autocomplete,
  createFilterOptions,
  FormControlLabel,
  Switch,
  Typography
} from '@mui/material'
import { isWindow } from '@/@core/utils/utils'
import dayjs, { Dayjs } from 'dayjs'

interface IconType {
  icon: CustomRadioIconsProps['icon']
  iconProps: CustomRadioIconsProps['iconProps']
}

interface FilmOptionType {
  inputValue?: string
  id?: number
  name: string
}
const filter = createFilterOptions<FilmOptionType>()

interface Props {
  sample: {
    sampleName?: string
    sampleSource?: string
    type?: string
    genomeBuild?: null | 'hg19' | 'hg38'
    projectId?: number | string[] | null
    genePanel?: number | null
    targetRegionId?: number[] | number | null
    primerBedId?: number[] | number | null
    prefix?: string
    fileSize?: number
  }
  patient: {
    patientName: string
    infirmaryName: string
    departmentName: string
    caseNumber: string
    sampleCode: string
    familyHistory: string
    phenotype: string
    resultsIn: null | string
    acceptDate: Dayjs | null | string
    birthday: Dayjs | null | string
    doctor: string
    gender: string | null
    race: string | null
    tooptip: string[]
    status: boolean
    mapq_threshold: number
    sentieonPipeline: SentieonPipelines
    pcrFree: boolean
    isNewPipeline: boolean
    removeDuplication: boolean
    hasSv: boolean
    svFile: string
    svName: string
    hasCnv: boolean
    cnvFile: string
    cnvName: string
    isFq: boolean
    fqFiles: {
      fileName: string
      process: number
      file?: File
      fileSize?: number
    }[]
  }
  fn?: any
  control: any
  errors: any
  setValue?: any
  sampleType?: string
}

const StepSample = React.forwardRef((props: Props, ref) => {
  const { t } = useTranslation()
  const fileType = props.sample.sampleName ?? '.vcf'
  const sampleType = props.sampleType

  const data: CustomRadioIconsData[] = [
    {
      isSelected: true,
      value: 'wes',
      title: 'WES'
    },
    {
      isSelected: false,
      value: 'wgs',
      title: 'WGS'
    },
    {
      value: 'gene_panel',
      title: t('gene') + 'Panel'
    },
    {
      value: 'microarray',
      title: 'MicroArray'
    },
    {
      value: 'amplicon',
      title: t('Multiplex PCR')
    }
  ]
  const initialIconSelected: string = data.filter((item) => item.isSelected)[
    data.filter((item) => item.isSelected).length - 1
  ].value

  const regionArray = [
    'East Asia',
    'Europe',
    'Africa',
    'Australia',
    'North America',
    'South America'
  ]

  const genomeBuildArray = ['hg19', 'hg38']
  const sampleSourceArray = [
    {
      title: 'DNA',
      key: 'DNA'
    },
    {
      title: t('saliva'),
      key: 'saliva'
    },
    {
      title: t('blood'),
      key: 'blood'
    },
    {
      title: t('mouth swab'),
      key: 'mouth-swab'
    },
    {
      title: t('amniotic fluid'),
      key: 'amniotic-fluid'
    },
    {
      title: t('umbilical blood'),
      key: 'umbilical-blood'
    },
    {
      title: t('aborted tissue'),
      key: 'aborted-tissue'
    }
  ]

  // ** States
  const [region, setRegion] = useState<string[]>([])
  const [sampleField, setSampleField] = useState({
    sample: {
      ...props.sample
    }
  })

  const [patientField, setPatientField] = useState({
    patient: {
      ...props.patient
    }
  })

  const messageRef = React.useRef<any>()
  const [openRol, setOpenRol] = React.useState<boolean>(false)
  const [openPanel, setOpenPanel] = React.useState<boolean>(false)
  const [openProject, setOpenProject] = React.useState<boolean>(false)
  const [value, setValue] = React.useState<FilmOptionType | null>(null)
  const [selectedRadio, setSelectedRadio] =
    useState<string>(initialIconSelected)

  // ** Hook
  const theme = useTheme()

  const icons: IconType[] = [
    {
      icon: 'mdi:tag-outline',
      iconProps: {
        fontSize: '2rem',
        style: { marginBottom: 4 },
        color: theme.palette.text.secondary
      }
    },
    {
      icon: 'mdi:tag-outline',
      iconProps: {
        fontSize: '2rem',
        style: { marginBottom: 4 },
        color: theme.palette.text.secondary
      }
    },
    {
      icon: 'mdi:tag-outline',
      iconProps: {
        fontSize: '2rem',
        style: { marginBottom: 4 },
        color: theme.palette.text.secondary
      }
    },
    {
      icon: 'mdi:tag-outline',
      iconProps: {
        fontSize: '2rem',
        style: { marginBottom: 4 },
        color: theme.palette.text.secondary
      }
    },
    {
      icon: 'mdi:tag-outline',
      iconProps: {
        fontSize: '2rem',
        style: { marginBottom: 4 },
        color: theme.palette.text.secondary
      }
    }
  ]

  const handleChange = (event: SelectChangeEvent<typeof region>) => {
    const {
      target: { value }
    } = event

    setRegion(typeof value === 'string' ? value.split(',') : value)
  }

  React.useEffect(() => {
    setPatientField({
      patient: {
        ...patientField.patient,
        fqFiles: props.patient.fqFiles,
        status: props.patient.status,
        isNewPipeline: props.patient.isNewPipeline,
        removeDuplication: props.patient.removeDuplication,
        pcrFree: props.patient.pcrFree
      }
    })
  }, [props.patient.isNewPipeline, props.patient.status, props.patient.fqFiles])

  React.useEffect(() => {
    setSelectedRadio(props.sample.type || initialIconSelected)

    //
  }, [props.sample.type])

  const handleRadioChange = (prop: string | ChangeEvent<HTMLInputElement>) => {
    if (typeof prop === 'string') {
      setSelectedRadio(prop)

      setSampleField({
        sample: {
          ...sampleField.sample,
          type: prop
        }
      })

      props.fn('sample', {
        ...sampleField.sample,
        type: prop
      })
    } else {
      setSampleField({
        sample: {
          ...sampleField.sample,
          type: (prop.target as HTMLInputElement).value
        }
      })

      props.fn('sample', {
        ...sampleField.sample,
        type: (prop.target as HTMLInputElement).value
      })

      setSelectedRadio((prop.target as HTMLInputElement).value)
    }
  }

  const { data: projects = [], refetch } = useQuery<Project[]>(
    'project',
    async () => await fetchProjects({ pageNum: 1, pageSize: 1000 }),
    {
      keepPreviousData: true
    }
  )

  const { data: genePanel, refetch: refetchPanel } = useQuery<GenePanel[]>(
    ['genePanel'],
    async () =>
      await getGenePanel({
        pageNum: 1,
        pageSize: 1000
      }),
    {
      keepPreviousData: true
    }
  )
  const { data: geneSets, refetch: refetchRol } = useQuery<GeneSets[]>(
    ['geneSets'],
    async () =>
      await getGeneSets({
        pageNum: 1,
        pageSize: 1000
      }),
    {
      keepPreviousData: true
    }
  )
  React.useEffect(() => {
    const obj = {
      sample: {
        ...sampleField.sample
      }
    }
    if (props.sample.sampleName) {
      obj.sample.sampleName = props.sample.sampleName

      props.setValue({
        sampleName: props.sample.sampleName
      })
    }
    if (props.sample.genePanel) {
      obj.sample.genePanel = props.sample.genePanel
    }
    if (props.sample.targetRegionId) {
      obj.sample.targetRegionId = props.sample.targetRegionId
    }
    if (props.sample.projectId && projects.length > 0) {
      const temp = projects.find(
        (item: any) => item.id === props.sample.projectId
      )
      obj.sample.projectId = props.sample.projectId

      props.setValue({
        projectId: props.sample.projectId
      })

      setValue({
        id: temp?.id,
        name: temp?.name ?? '',
        inputValue: temp?.name ?? ''
      })
    }
    setSampleField(obj)
  }, [
    props.sample.sampleName,
    props.sample.projectId,
    props.sample.genePanel,
    props.sample.targetRegionId,
    projects
  ])

  const handleSaveGeneSets = (geneSet: any) => {
    if ('name' in geneSet && 'id' in geneSet) {
      updateGeneSetsMutation.mutate(geneSet)
    } else {
      createGeneSetsMutation.mutate(geneSet)
    }
  }

  const handleSaveGenePanel = (project: any) => {
    if ('name' in project && 'id' in project) {
      editGenePanelMutation.mutate(project)
    } else {
      createGenePanelMutation.mutate(project)
    }
  }

  const handleSaveProject = (project: any) => {
    if ('name' in project && 'description' in project && 'id' in project) {
      updateProjectMutation.mutate(project)
    } else {
      createProjectMutation.mutate(project)
    }
  }

  const createProjectMutation = useMutation(createProject, {
    onSuccess: () => {
      setOpenProject(false)

      messageRef?.current?.handleClick(t('New successful'), 'success')

      refetch()
    },
    onError: (err: any) => {
      //

      messageRef?.current?.handleClick(
        err?.response?.data?.message ?? '请重试',
        'error'
      )
    }
  })
  const updateProjectMutation = useMutation(updateProject, {
    onSuccess: () => {
      setOpenProject(false)

      messageRef?.current?.handleClick(t('Edit success'), 'success')

      refetch()
    }
  })

  const createGenePanelMutation = useMutation(createGenePanel, {
    onSuccess: () => {
      setOpenPanel(false)

      messageRef?.current?.handleClick(t('New successful'), 'success')

      refetchPanel()
    }
  })
  const editGenePanelMutation = useMutation(editGenePanel, {
    onSuccess: () => {
      setOpenPanel(false)

      messageRef?.current?.handleClick(t('Edit success'), 'success')

      refetchPanel()
    }
  })

  const createGeneSetsMutation = useMutation(createGeneSets, {
    onSuccess: () => {
      setOpenRol(false)

      refetchRol()
    }
  })
  const updateGeneSetsMutation = useMutation(editGeneSets, {
    onSuccess: () => {
      setOpenRol(false)

      refetchRol()
    }
  })

  React.useImperativeHandle(ref, () => {
    return {
      sampleField
    }
  })

  return (
    <React.Fragment>
      <Grid container spacing={5}>
        <Grid item xs={12} sm={12}>
          <Box className={'flex items-center gap-2'} alignItems="baseline">
            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={patientField.patient.pcrFree}
                    onChange={(e: any) => {
                      setPatientField({
                        patient: {
                          ...patientField.patient,
                          pcrFree: e.target.checked
                        }
                      })

                      props.fn('patient', {
                        ...patientField.patient,
                        pcrFree: e.target.checked
                      })
                    }}
                  />
                }
                labelPlacement="start"
                label={t('PCR Free or not')}
                sx={{
                  display: 'flex',
                  justifyContent: 'start'
                }}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={patientField.patient.status}
                    onChange={(e: any) => {
                      setPatientField({
                        patient: {
                          ...patientField.patient,
                          status: e.target.checked
                        }
                      })

                      props.fn('patient', {
                        ...patientField.patient,
                        status: e.target.checked
                      })
                    }}
                  />
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'start'
                }}
                labelPlacement="start"
                label={t('Immediate analysis or not')}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={patientField.patient.isNewPipeline}
                    onChange={(e: any) => {
                      setPatientField({
                        patient: {
                          ...patientField.patient,
                          isNewPipeline: e.target.checked
                        }
                      })

                      props.fn('patient', {
                        ...patientField.patient,
                        isNewPipeline: e.target.checked
                      })
                    }}
                  />
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'start'
                }}
                labelPlacement="start"
                label={t('Whether to use the new pipeline')}
              />
            </FormControl>

            {props.sampleType === 'fastq' || props.sampleType === 'naf' ? (
              <FormControl fullWidth sx={{ mb: 4, textAlign: 'left' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={patientField.patient.removeDuplication}
                      onChange={(e: any) => {
                        setPatientField({
                          patient: {
                            ...patientField.patient,
                            removeDuplication: e.target.checked
                          }
                        })

                        props.fn('patient', {
                          ...patientField.patient,
                          removeDuplication: e.target.checked
                        })
                      }}
                    />
                  }
                  sx={{
                    display: 'flex',
                    justifyContent: 'start'
                  }}
                  labelPlacement="start"
                  label={t('Remove Duplicates or not')}
                />
              </FormControl>
            ) : (
              ''
            )}
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          display="flex"
          justifyContent="flex-start"
          spacing={5}
        >
          {data.map((item, index) => (
            <CustomRadioIcons
              key={index}
              data={data[index]}
              icon={icons[index].icon}
              selected={selectedRadio}
              name="custom-radios-deal"
              gridProps={{ sm: 2.4, xs: 12 }}
              handleChange={handleRadioChange}
              iconProps={icons[index].iconProps}
            />
          ))}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={'flex items-center gap-2'} alignItems="baseline">
            <Typography
              className="font-medium"
              color="text.primary"
              sx={{
                wordBreak: 'keep-all',
                mr: 2
              }}
              flex={2}
              textAlign="right"
            >
              {t('Sample name')}
              <span style={{ color: 'red' }}>*</span>:
            </Typography>

            <FormControl fullWidth sx={{ mb: 4, flex: isWindow() ? 7 : 5 }}>
              <Controller
                name="sampleName"
                control={props.control}
                rules={{ required: true }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextField
                    autoFocus
                    size={'small'}
                    value={value}
                    onBlur={onBlur}
                    onChange={(e) => {
                      setSampleField({
                        sample: {
                          ...sampleField.sample,
                          sampleName: e.target.value
                        }
                      })

                      props.fn('sample', {
                        ...sampleField.sample,
                        sampleName: e.target.value
                      })

                      onChange(e)
                    }}
                    error={Boolean(props.errors.sampleName)}
                  />
                )}
              />

              {props.errors.sampleName && (
                <FormHelperText sx={{ color: 'error.main' }}>
                  {props.errors.sampleName.message}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={'flex items-center gap-2'} alignItems="baseline">
            <Typography
              className="font-medium"
              color="text.primary"
              sx={{
                wordBreak: 'keep-all',
                mr: 2
              }}
              flex={2}
              textAlign="right"
            >
              {t('Sample Source')}:
            </Typography>

            <FormControl fullWidth sx={{ mb: 4, flex: isWindow() ? 7 : 5 }}>
              <Select
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={sampleField.sample.sampleSource}
                onChange={(e: any) => {
                  setSampleField({
                    sample: {
                      ...sampleField.sample,
                      sampleSource: e.target.value
                    }
                  })

                  props.fn('sample', {
                    ...sampleField.sample,
                    sampleSource: e.target.value
                  })
                }}
                error={Boolean(props.errors.projectId)}
                IconComponent={ExpandCircleDownOutlinedIcon}
                size={'small'}
              >
                {sampleSourceArray.map((sampleSource) => (
                  <MenuItem key={sampleSource.key} value={sampleSource.key}>
                    {sampleSource.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={'flex items-center gap-2'} alignItems="baseline">
            <Typography
              className="font-medium"
              color="text.primary"
              sx={{
                wordBreak: 'keep-all',
                mr: 2
              }}
              flex={2}
              textAlign="right"
            >
              {t('Reference Genome')}
              <span style={{ color: 'red' }}>*</span>:
            </Typography>

            <Box sx={{ flex: isWindow() ? 7 : 5 }}>
              <FormControl fullWidth>
                <Select
                  labelId="select-user-type"
                  value={sampleField.sample.genomeBuild}
                  onChange={(e) => {
                    setSampleField({
                      sample: {
                        ...sampleField.sample,
                        genomeBuild: e.target.value as 'hg19' | 'hg38'
                      }
                    })

                    props.fn('sample', {
                      ...sampleField.sample,
                      genomeBuild: e.target.value
                    })
                  }}
                  IconComponent={ExpandCircleDownOutlinedIcon}
                  size={'small'}
                >
                  {genomeBuildArray.map((genomeBuild) => (
                    <MenuItem key={genomeBuild} value={genomeBuild}>
                      {genomeBuild}
                    </MenuItem>
                  ))}
                </Select>

                <FormHelperText>
                  <Translations text="Select Genome Build"></Translations>
                </FormHelperText>
              </FormControl>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={'flex items-center gap-2'} alignItems="center">
            <Typography
              className="font-medium"
              color="text.primary"
              sx={{
                wordBreak: 'keep-all',
                mr: 2
              }}
              flex={2}
              textAlign="right"
            >
              {t('project')}:
            </Typography>

            <FormControl fullWidth sx={{ flex: isWindow() ? 7 : 5 }}>
              <Autocomplete
                fullWidth
                value={value}
                onChange={async (event, newValue) => {
                  //

                  if (typeof newValue !== 'string' && newValue && newValue.id) {
                    setValue(newValue)

                    setSampleField({
                      sample: {
                        ...sampleField.sample,
                        projectId: newValue.id
                      }
                    })

                    props.fn('sample', {
                      ...sampleField.sample,
                      projectId: newValue.id
                    })
                  } else {
                    if (typeof newValue === 'string') {
                      setValue({
                        name: newValue
                      })

                      const params = {
                        name: newValue,
                        isPublic: 0 as 0 | 1,
                        createdAt: new Date(),
                        description: ''
                      }
                      const project = await createProjectMutation.mutateAsync(
                        params
                      )

                      const temp = project.find(
                        (item: any) => item.name === params.name
                      )

                      //

                      setSampleField({
                        sample: {
                          ...sampleField.sample,
                          projectId: temp.id
                        }
                      })

                      props.fn('sample', {
                        ...sampleField.sample,
                        projectId: temp.id
                      })
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setValue({
                        name: newValue.inputValue
                      })
                      const params = {
                        name: newValue.inputValue,
                        isPublic: 0 as 0 | 1,
                        createdAt: new Date(),
                        description: ''
                      }
                      const project = await createProjectMutation.mutateAsync(
                        params
                      )

                      const temp = project.find(
                        (item: any) => item.name === params.name
                      )

                      //

                      setSampleField({
                        sample: {
                          ...sampleField.sample,
                          projectId: temp.id
                        }
                      })

                      props.fn('sample', {
                        ...sampleField.sample,
                        projectId: temp.id
                      })
                    } else {
                      setValue(newValue)
                    }
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params)

                  const { inputValue } = params

                  // Suggest the creation of a new value
                  const isExisting = options.some(
                    (option) => inputValue === option.name
                  )

                  if (inputValue !== '' && !isExisting) {
                    filtered.push({
                      inputValue,
                      name: `添加 "${inputValue}"`
                    })
                  }

                  return filtered
                }}
                size="small"
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                id="free-solo-with-text-demo"
                options={
                  (projects &&
                    projects.map((item) => {
                      return {
                        id: item.id,
                        name: item.name,
                        inputValue: item.name
                      }
                    })) ??
                  []
                }
                getOptionLabel={(option) => {
                  // Value selected with enter, right from the input
                  if (typeof option === 'string') {
                    return option
                  }

                  // Add "xxx" option created dynamically
                  if (option.name) {
                    return option.name
                  }

                  // Regular option
                  return option.name
                }}
                renderOption={(props, option) => {
                  return <li {...props}>{option.name}</li>
                }}
                freeSolo
                renderInput={(params) => <TextField {...params} />}
              />

              {/* <Controller
                name="projectId"
                control={props.control}
                rules={{ required: true }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <React.Fragment>
                    <Select
                      labelId="demo-simple-select-standard-label"
                      id="demo-simple-select-standard"
                      value={value}
                      autoFocus
                      onChange={(e: any) => {
                        setSampleField({
                          sample: {
                            ...sampleField.sample,
                            projectId: e.target.value
                          }
                        })

                        props.fn('sample', {
                          ...sampleField.sample,
                          projectId: e.target.value
                        })

                        onChange(e)
                      }}
                      onBlur={onBlur}
                      size={'small'}
                      IconComponent={ExpandCircleDownOutlinedIcon}
                      error={Boolean(props.errors.projectId)}
                    >
                      {projects &&
                        projects.map((reg) => (
                          <MenuItem key={reg.id} value={reg.id}>
                            {reg.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </React.Fragment>
                )}
              />

              {props.errors.projectId && (
                <FormHelperText sx={{ color: 'error.main' }}>
                  {props.errors.projectId.message}
                </FormHelperText>
              )}

              <FormHelperText
                sx={{
                  cursor: 'pointer',
                  color: 'primary.main'
                }}
                onClick={() => {
                  setOpenProject(true)
                }}
              >
                {`${t('create')} ${t('project')}`}
              </FormHelperText> */}
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={'flex items-center gap-2'} alignItems="baseline">
            <Typography
              className="font-medium"
              color="text.primary"
              sx={{
                wordBreak: 'keep-all',
                mr: 2
              }}
              flex={2}
              textAlign="right"
            >
              {t('gene')} Panel:
            </Typography>

            <FormControl fullWidth sx={{ mb: 4, flex: isWindow() ? 7 : 5 }}>
              <Select
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={sampleField.sample.genePanel}
                onChange={(e: any) => {
                  if (e.target.value) {
                    setSampleField({
                      sample: {
                        ...sampleField.sample,
                        genePanel: e.target.value ? e.target.value : null
                      }
                    })

                    props.fn('sample', {
                      ...sampleField.sample,
                      genePanel: e.target.value ? e.target.value : null
                    })
                  }
                }}
                size={'small'}
                IconComponent={ExpandCircleDownOutlinedIcon}
                error={Boolean(props.errors.genePanel)}
              >
                {genePanel?.length ? (
                  genePanel?.map((panel) => (
                    <MenuItem key={panel.id} value={panel.id}>
                      {panel.panelName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem key={''} value={''}>
                    {t('No data available')}
                  </MenuItem>
                )}
              </Select>

              {props.errors.genePanel && (
                <FormHelperText sx={{ color: 'error.main' }}>
                  {props.errors.genePanel.message}
                </FormHelperText>
              )}

              <FormHelperText
                sx={{
                  cursor: 'pointer',
                  color: 'primary.main'
                }}
                onClick={() => {
                  setOpenPanel(true)
                }}
              >
                {t('upload')}
                Panel
              </FormHelperText>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={'flex items-center gap-2'} alignItems="baseline">
            <Typography
              className="font-medium"
              color="text.primary"
              sx={{
                wordBreak: 'keep-all',
                mr: 2
              }}
              flex={2}
              textAlign="right"
            >
              {t('Gene Set')}:
            </Typography>

            <FormControl fullWidth sx={{ mb: 4, flex: isWindow() ? 7 : 5 }}>
              <Select
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={sampleField.sample.targetRegionId as any}
                onChange={(e: any) => {
                  //

                  if (e.target.value || e.target.value === '') {
                    setSampleField({
                      sample: {
                        ...sampleField.sample,
                        targetRegionId: e.target.value ? e.target.value : null
                      }
                    })

                    props.fn('sample', {
                      ...sampleField.sample,
                      targetRegionId: e.target.value ? e.target.value : null
                    })
                  }
                }}
                size={'small'}
                IconComponent={ExpandCircleDownOutlinedIcon}
                error={Boolean(props.errors.targetRegionId)}
              >
                <MenuItem key={''} value={''}>
                  {t('null')}
                </MenuItem>

                {geneSets?.length ? (
                  geneSets
                    ?.filter((i) => i.type === 'target')
                    ?.map((set) => (
                      <MenuItem key={set.id} value={`${set.id}`}>
                        {set.name}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem key={''} value={''}>
                    {t('No data available')}
                  </MenuItem>
                )}
              </Select>

              {props.errors.targetRegionId && (
                <FormHelperText sx={{ color: 'error.main' }}>
                  {props.errors.targetRegionId.message}
                </FormHelperText>
              )}

              <FormHelperText
                sx={{
                  cursor: 'pointer',
                  color: 'primary.main'
                }}
                onClick={() => {
                  setOpenRol(true)
                }}
              >
                {t('upload')}Bed
                <span>
                  {t(
                    'It is recommended to upload or select the appropriate sequencing vendor BED file'
                  )}
                </span>
              </FormHelperText>

              {/* <FormHelperText>
              建议上传或者选择合适的测序厂商BED文件
            </FormHelperText> */}
            </FormControl>
          </Box>
        </Grid>

        {selectedRadio === 'amplicon' ? (
          <Grid item xs={12} sm={6}>
            <Box className={'flex items-center gap-2'} alignItems="baseline">
              <Typography
                className="font-medium"
                color="text.primary"
                sx={{
                  wordBreak: 'keep-all',
                  mr: 2
                }}
                flex={2}
                textAlign="right"
              >
                {t('Amplification primers')}:
              </Typography>

              <FormControl fullWidth sx={{ mb: 4, flex: isWindow() ? 7 : 5 }}>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  value={sampleField.sample.primerBedId as any}
                  onChange={(e: any) => {
                    //

                    if (e.target.value || e.target.value === '') {
                      setSampleField({
                        sample: {
                          ...sampleField.sample,
                          primerBedId: e.target.value ? e.target.value : null
                        }
                      })

                      props.fn('sample', {
                        ...sampleField.sample,
                        primerBedId: e.target.value ? e.target.value : null
                      })
                    }
                  }}
                  size={'small'}
                  IconComponent={ExpandCircleDownOutlinedIcon}
                  error={Boolean(props.errors.primerBedId)}
                >
                  <MenuItem key={''} value={''}>
                    {t('null')}
                  </MenuItem>

                  {geneSets?.length ? (
                    geneSets
                      ?.filter((i) => i.type === 'primer')
                      ?.map((set) => (
                        <MenuItem key={set.id} value={`${set.id}`}>
                          {set.name}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem key={''} value={''}>
                      {t('No data available')}
                    </MenuItem>
                  )}
                </Select>

                {props.errors.primerBedId && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {props.errors.primerBedId.message}
                  </FormHelperText>
                )}

                <FormHelperText
                  sx={{
                    cursor: 'pointer',
                    color: 'primary.main'
                  }}
                  onClick={() => {
                    setOpenRol(true)
                  }}
                >
                  {t('upload')}Bed
                  <span>
                    {t(
                      'It is recommended to upload or select the appropriate sequencing vendor BED file'
                    )}
                  </span>
                </FormHelperText>

                {/* <FormHelperText>
              建议上传或者选择合适的测序厂商BED文件
            </FormHelperText> */}
              </FormControl>
            </Box>
          </Grid>
        ) : (
          ''
        )}

        <PositionedSnackbar ref={messageRef} />

        <RolDialog
          rolToEdit={null}
          open={openRol}
          onClose={() => {
            setOpenRol(false)
          }}
          onSave={handleSaveGeneSets}
        />

        <PanelDialog
          panelToEdit={null}
          open={openPanel}
          onClose={() => {
            setOpenPanel(false)
          }}
          onSave={handleSaveGenePanel}
          type={'change'}
        />

        <ProjectDialog
          projectToEdit={null}
          open={openProject}
          onClose={() => {
            setOpenProject(false)
          }}
          onSave={handleSaveProject}
        />
      </Grid>
    </React.Fragment>
  )
})

export default StepSample
